import type {
  MetricComparisonKind,
  MetricComparisonResponse,
  QuickBooksFpaMlDriverAssumptionResponse,
  QuickBooksFpaStatementRender,
  QuickBooksFpaStatementRow,
} from '../../../data/ServerApiClient'

export const driverNumber = (assumptions: any, key: string, field = 'expected'): number => {
  const value = assumptions?.[key]?.[field]
  const n = Number(value)
  return Number.isFinite(n) ? n : 0
}

export type ForecastDriverMetricBasis = {
  grossMargin: number | null
  opexPct: number | null
}

export const cloneRowWithValues = (row: QuickBooksFpaStatementRow, values: Array<number | null>): QuickBooksFpaStatementRow => ({
  ...row,
  values,
})

export const emptyValues = (count: number): Array<number | null> => Array.from({ length: Math.max(0, count) }, () => null)

export const hasStatementValue = (values: Array<number | null | undefined>): boolean =>
  values.some((value) => Math.abs(Number(value || 0)) > 0.005)

export const addVectors = (...vectors: Array<Array<number | null | undefined>>): Array<number | null> => {
  const length = Math.max(0, ...vectors.map((vector) => vector.length))
  return Array.from({ length }, (_, index) => {
    const value = vectors.reduce((sum, vector) => sum + Number(vector[index] || 0), 0)
    return Math.abs(value) > 0.005 ? value : null
  })
}

const medianNumber = (values: number[]): number | null => {
  const sorted = values.filter((value) => Number.isFinite(value)).sort((a, b) => a - b)
  if (!sorted.length) return null
  const mid = Math.floor(sorted.length / 2)
  return sorted.length % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid]
}

/**
 * A stable "anticipated" anchor value from recent actuals, robust to an abnormal
 * last month: take the median of the last `windowSize` actuals; if the latest is
 * within ±15% of that median, trust it; otherwise blend back toward the median
 * (65% median + 35% of the latest, clamped to ±10% of median). Shared by the
 * online direct-cash roll-forward and the export's driver-model seed so the two
 * anchor identically.
 */
export const representativeRecentActualValue = (
  values: Array<number | null | undefined>,
  periodKinds: string[],
  beforeIndex: number,
  windowSize = 6,
): number | null => {
  const actualValues = periodKinds
    .map((kind, index) => {
      if (kind !== 'actual' || index >= beforeIndex) return null
      const value = Math.abs(Number(values[index]))
      return Number.isFinite(value) && value > 0.005 ? value : null
    })
    .filter((value): value is number => value != null)
    .slice(-windowSize)
  if (!actualValues.length) return null
  const latest = actualValues[actualValues.length - 1]
  if (actualValues.length < 3) return latest
  const center = medianNumber(actualValues)
  if (center == null || center <= 0.005) return latest
  if (latest >= center * 0.85 && latest <= center * 1.15) return latest
  const clampedLatest = Math.max(center * 0.90, Math.min(center * 1.10, latest))
  return center * 0.65 + clampedLatest * 0.35
}

export const scaleVector = (values: Array<number | null | undefined>, factor: number): Array<number | null> =>
  values.map((value) => {
    const next = Number(value || 0) * factor
    return Math.abs(next) > 0.005 ? next : null
  })

export const signedAbsVector = (values: Array<number | null | undefined>, sign: 1 | -1): Array<number | null> =>
  values.map((value) => {
    const next = Math.abs(Number(value || 0)) * sign
    return Math.abs(next) > 0.005 ? next : null
  })

export const addVectorInto = (target: Map<string, Array<number | null>>, key: string, values: Array<number | null | undefined>, periodCount: number) => {
  const current = target.get(key) || emptyValues(periodCount)
  target.set(key, addVectors(current, values))
}

export const metricValue = (comparisons: MetricComparisonResponse | null, ids: string[]): number | null => {
  const wanted = new Set(ids.map((id) => id.toLowerCase()))
  const row = comparisons?.metrics?.find((metric) => wanted.has(String(metric.id || '').toLowerCase()))
  return Number.isFinite(row?.latest_value) ? Number(row?.latest_value) : null
}

const metricPctOfSales = (comparisons: MetricComparisonResponse | null, ids: string[]): number | null => {
  const wanted = new Set(ids.map((id) => id.toLowerCase()))
  const row = comparisons?.metrics?.find((metric) => wanted.has(String(metric.id || '').toLowerCase()))
  return normalizeDriverRatio(row?.latest_pct_of_sales)
}

const metricDeltaPct = (comparisons: MetricComparisonResponse | null, ids: string[], comparisonKind: MetricComparisonKind): number | null => {
  const wanted = new Set(ids.map((id) => id.toLowerCase()))
  const row = comparisons?.metrics?.find((metric) => wanted.has(String(metric.id || '').toLowerCase()))
  const comparison = row?.comparisons?.find((item) => item.kind === comparisonKind)
  return Number.isFinite(comparison?.delta_pct) ? Number(comparison?.delta_pct) : null
}

export const hasStoredDriverAssumptions = (response: QuickBooksFpaMlDriverAssumptionResponse | null): boolean =>
  Boolean((response?.package as any)?.validation_json?.driver_assumptions_updated_at)

const driverFieldNumber = (assumptions: any, key: string, field = 'expected'): number | null => {
  const value = assumptions?.[key]?.[field]
  const n = Number(value)
  return Number.isFinite(n) ? n : null
}

const normalizeDriverRatio = (value: unknown): number | null => {
  const numeric = Number(value)
  if (!Number.isFinite(numeric)) return null
  return Math.abs(numeric) > 2 ? numeric / 100 : numeric
}

const driverExpected = (current: any, key: string, fallback: number | null, defaultValue = 0, preferExisting = false, keepGenerated = false): any => {
  const existing = driverFieldNumber(current, key)
  const isUserOverride = current?.[key]?.source === 'user_override'
  const shouldKeepExisting = existing != null && (
    isUserOverride
    || preferExisting
    || (keepGenerated && Math.abs(existing) > 0.000001)
    || (fallback == null && Math.abs(existing) > 0.000001)
  )
  const value = shouldKeepExisting ? existing : fallback ?? existing ?? defaultValue
  return { ...(current?.[key] || {}), expected: value }
}

const statementRowByLabels = (
  statement: QuickBooksFpaStatementRender | null | undefined,
  labelSets: string[][],
): QuickBooksFpaStatementRow | null => {
  const rows = statement?.rows || []
  for (const labels of labelSets) {
    const candidates = rows.filter((row) => {
      const text = String(row.label || '').toLowerCase()
      return labels.every((label) => text.includes(label))
    })
    const best = candidates
      .filter((row) => hasStatementValue(row.values))
      .sort((a, b) => {
        const totalScore = (row: QuickBooksFpaStatementRow) => {
          const text = normalizeForecastRowLabel(row.label)
          const valueScore = Math.abs(row.values.reduce<number>((sum, value) => sum + Number(value || 0), 0))
          const exactLabel = labels.join(' ')
          const exactMatch = text === exactLabel || text === `total ${exactLabel}`
          return valueScore
            + (exactMatch ? 10_000_000_000 : 0)
            + (Number(row.level || 0) === 0 ? 1_000_000_000 : 0)
            + (row.is_total || row.is_formula || /^total\s+/.test(text) ? 100_000_000 : 0)
            + (row.row_type === 'group' || row.row_type === 'formula' ? 10_000_000 : 0)
        }
        return totalScore(b) - totalScore(a)
      })[0]
    if (best) return best
  }
  return null
}

const finiteRowValues = (row: QuickBooksFpaStatementRow | null | undefined): number[] => {
  const values = (row?.values || []).map((value) => Number(value)).filter((value) => Number.isFinite(value))
  const firstObserved = values.findIndex((value) => Math.abs(value) > 0.005)
  return firstObserved >= 0 ? values.slice(firstObserved) : []
}

const windowSum = (values: number[], start: number, count: number): number | null => {
  if (start < 0 || count <= 0 || start + count > values.length) return null
  const slice = values.slice(start, start + count)
  if (slice.length !== count) return null
  return slice.reduce((sum, value) => sum + value, 0)
}

const growthFromValues = (latest: number | null, prior: number | null): number | null => {
  if (latest == null || prior == null || Math.abs(prior) < 0.01) return null
  const ratio = latest / prior
  return Number.isFinite(ratio) && ratio > 0 ? ratio - 1 : null
}

const trailingTwelveGrowth = (row: QuickBooksFpaStatementRow | null | undefined): number | null => {
  const values = finiteRowValues(row)
  if (values.length < 24) return null
  const latest = windowSum(values, values.length - 12, 12)
  const prior = windowSum(values, values.length - 24, 12)
  return growthFromValues(latest, prior)
}

const recentWindowGrowth = (row: QuickBooksFpaStatementRow | null | undefined): number | null => {
  const values = finiteRowValues(row)
  if (values.length < 6) return null
  const latest = windowSum(values, values.length - 3, 3)
  const prior = windowSum(values, values.length - 6, 3)
  return growthFromValues(latest, prior)
}

const trailingRatio = (
  numeratorRow: QuickBooksFpaStatementRow | null | undefined,
  denominatorRow: QuickBooksFpaStatementRow | null | undefined,
  months = 12,
): number | null => {
  const numerator = finiteRowValues(numeratorRow)
  const denominator = finiteRowValues(denominatorRow)
  const count = Math.min(months, numerator.length, denominator.length)
  if (count < 3) return null
  const numeratorSum = windowSum(numerator, numerator.length - count, count)
  const denominatorSum = windowSum(denominator, denominator.length - count, count)
  if (numeratorSum == null || denominatorSum == null || Math.abs(denominatorSum) < 0.01) return null
  return Math.abs(numeratorSum) / Math.abs(denominatorSum)
}

const daysOutstandingFromRows = (
  balanceRow: QuickBooksFpaStatementRow | null | undefined,
  flowRow: QuickBooksFpaStatementRow | null | undefined,
  months = 12,
): number | null => {
  const balanceValues = finiteRowValues(balanceRow)
  const flowValues = finiteRowValues(flowRow)
  if (!balanceValues.length || !flowValues.length) return null
  const latestBalance = Math.abs(balanceValues[balanceValues.length - 1])
  const count = Math.min(months, flowValues.length)
  if (count < Math.min(months, 3)) return null
  const flowSum = flowValues.slice(flowValues.length - count).reduce((sum, value) => sum + Math.abs(value), 0)
  if (flowSum < 0.01) return null
  return latestBalance * (count * (365.25 / 12)) / flowSum
}

const indexedRowSum = (row: QuickBooksFpaStatementRow | null | undefined, indexes: number[]): number | null => {
  if (!row || !indexes.length) return null
  let sum = 0
  let count = 0
  for (const index of indexes) {
    const value = Number(row.values?.[index])
    if (!Number.isFinite(value)) continue
    sum += value
    count += 1
  }
  return count ? sum : null
}

const indexedRatio = (
  numeratorRow: QuickBooksFpaStatementRow | null | undefined,
  denominatorRow: QuickBooksFpaStatementRow | null | undefined,
  indexes: number[],
): number | null => {
  const numeratorSum = indexedRowSum(numeratorRow, indexes)
  const denominatorSum = indexedRowSum(denominatorRow, indexes)
  if (numeratorSum == null || denominatorSum == null || Math.abs(denominatorSum) < 0.01) return null
  return Math.abs(numeratorSum) / Math.abs(denominatorSum)
}

export const forecastDriverMetricsFromStatement = (
  statement: QuickBooksFpaStatementRender | null | undefined,
): ForecastDriverMetricBasis => {
  if (statement?.statement_type !== 'profit_and_loss') return { grossMargin: null, opexPct: null }
  const revenue = statementRowByLabels(statement, [['total', 'revenue'], ['total', 'income'], ['revenue'], ['income']])
  const grossProfit = statementRowByLabels(statement, [['gross', 'profit']])
  const costOfSales = statementRowByLabels(statement, [['cost', 'of', 'sales'], ['cost', 'of', 'goods'], ['cost', 'of', 'revenue'], ['cogs'], ['total', 'cost']])
  const opex = statementRowByLabels(statement, [['operating', 'expenses'], ['total', 'expenses'], ['expenses'], ['expense']])
  const periodKinds = ((statement as any)?.period_kinds || []) as string[]
  const forecastIndexes = periodKinds.length
    ? periodKinds.map((kind, index) => kind === 'forecast' ? index : -1).filter((index) => index >= 0)
    : (statement.periods || []).map((_period, index) => index)
  const metricIndexes = forecastIndexes.slice(0, Math.min(12, forecastIndexes.length))
  const cogsPct = indexedRatio(costOfSales, revenue, metricIndexes)
  const grossProfitPct = indexedRatio(grossProfit, revenue, metricIndexes)
  return {
    grossMargin: cogsPct != null ? Math.max(0, 1 - cogsPct) : grossProfitPct,
    opexPct: indexedRatio(opex, revenue, metricIndexes),
  }
}

/** Count of closed actual months (finite revenue values) feeding the driver grounding. */
export const closedActualMonthCount = (actualBasis: QuickBooksFpaStatementRender | null | undefined): number => {
  const revenue = statementRowByLabels(actualBasis, [['total', 'revenue'], ['total', 'income'], ['revenue'], ['income']])
  if (!revenue?.values?.length) return 0
  return revenue.values.filter((v) => typeof v === 'number' && Number.isFinite(v)).length
}

export const historicalDriverMetrics = (
  actualBasis: QuickBooksFpaStatementRender | null | undefined,
  actualBalanceSheet: QuickBooksFpaStatementRender | null | undefined,
) => {
  const revenue = statementRowByLabels(actualBasis, [['total', 'revenue'], ['total', 'income'], ['revenue'], ['income']])
  const grossProfit = statementRowByLabels(actualBasis, [['gross', 'profit']])
  const opex = statementRowByLabels(actualBasis, [['operating', 'expenses'], ['total', 'expenses'], ['expense']])
  const costOfSales = statementRowByLabels(actualBasis, [['cost', 'of', 'sales'], ['cost', 'of', 'goods'], ['cost', 'of', 'revenue'], ['cogs'], ['total', 'cost']])
  const accountsReceivable = statementRowByLabels(actualBalanceSheet, [['accounts', 'receivable'], ['receivable']])
  const accountsPayable = statementRowByLabels(actualBalanceSheet, [['accounts', 'payable'], ['payable']])
  const cogsPct = trailingRatio(costOfSales, revenue)
  const grossProfitPct = trailingRatio(grossProfit, revenue)
  return {
    revenueGrowth: (trailingTwelveGrowth(revenue) ?? recentWindowGrowth(revenue)),
    grossMargin: cogsPct != null ? Math.max(0, 1 - cogsPct) : grossProfitPct,
    dsoDays: daysOutstandingFromRows(accountsReceivable, revenue),
    dpoDays: daysOutstandingFromRows(accountsPayable, costOfSales || opex),
    opexPct: trailingRatio(opex, revenue),
  }
}

export const observedDriverAssumptions = (
  response: QuickBooksFpaMlDriverAssumptionResponse | null,
  comparisons: MetricComparisonResponse | null,
  comparisonKind: MetricComparisonKind,
  actualBasis: QuickBooksFpaStatementRender | null,
  actualBalanceSheet: QuickBooksFpaStatementRender | null = null,
  mlForecastBasis: ForecastDriverMetricBasis | null = null,
) => {
  const current = response?.assumptions || {}
  const history = historicalDriverMetrics(actualBasis, actualBalanceSheet)
  const revenueGrowth = history.revenueGrowth ?? metricDeltaPct(comparisons, ['revenue', 'total_revenue', 'income'], comparisonKind)
  const grossMargin = history.grossMargin ?? mlForecastBasis?.grossMargin ?? normalizeDriverRatio(metricValue(comparisons, ['gross_margin']))
  const dso = history.dsoDays ?? metricValue(comparisons, ['dso', 'days_sales_outstanding'])
  const dpo = history.dpoDays ?? metricValue(comparisons, ['dpo', 'days_payable_outstanding'])
  const opex = history.opexPct ?? mlForecastBasis?.opexPct ?? metricPctOfSales(comparisons, ['operating_expenses', 'opex'])
  const preferExisting = hasStoredDriverAssumptions(response)
  const keepGeneratedDso = current?.dso_days?.source === 'trailing_balance_flow_history' || current?.dso_days?.source === 'trailing_balance_flow_trend'
  const keepGeneratedDpo = current?.dpo_days?.source === 'trailing_balance_flow_history' || current?.dpo_days?.source === 'trailing_balance_flow_trend'
  return {
    ...current,
    sales_growth_pct: driverExpected(current, 'sales_growth_pct', revenueGrowth, 0, false),
    gross_margin_pct: driverExpected(current, 'gross_margin_pct', grossMargin, 0, false),
    dso_days: driverExpected(current, 'dso_days', dso, 30, preferExisting || keepGeneratedDso),
    dpo_days: driverExpected(current, 'dpo_days', dpo, 30, preferExisting || keepGeneratedDpo),
    opex_pct: driverExpected(current, 'opex_pct', opex, 0, false),
    seasonality_strength: driverExpected(current, 'seasonality_strength', null, 1, preferExisting, true),
  }
}

export const statementLineValue = (
  statement: QuickBooksFpaStatementRender | null,
  labelPattern: RegExp,
): number | null => {
  const row = statement?.rows?.find((candidate) => labelPattern.test(candidate.label || ''))
  if (!row?.values?.length) return null
  for (let index = row.values.length - 1; index >= 0; index -= 1) {
    const value = Number(row.values[index])
    if (Number.isFinite(value)) return value
  }
  return null
}

const clampFactor = (value: number, min = 0.35, max = 1.65) => Math.max(min, Math.min(max, value))
const annualGrowthFactor = (growth: number, monthNumber: number): number =>
  Math.pow(Math.max(0.01, 1 + growth), Math.max(0, monthNumber) / 12)

type ForecastProfitAndLossBucket = 'income' | 'costOfGoodsSold' | 'expenses' | 'otherIncome' | 'otherExpenses'

export const normalizeForecastRowLabel = (value: unknown): string =>
  String(value || '').trim().toLowerCase().replace(/[_-]+/g, ' ').replace(/\s+/g, ' ')

const forecastProfitAndLossBucket = (row: QuickBooksFpaStatementRow): ForecastProfitAndLossBucket | null => {
  const label = normalizeForecastRowLabel(row.label)
  if (/^(total\s+)?(gross profit|operating income|net operating income|net income|net earnings|ebitda)$/.test(label)) return null
  const accountType = normalizeForecastRowLabel(row.qbo_account_type)
  const accountSubType = normalizeForecastRowLabel(row.qbo_account_sub_type)
  const classification = normalizeForecastRowLabel(row.qbo_classification)
  const typed = `${accountType} ${accountSubType} ${classification}`
  if (typed.includes('cost of goods sold') || typed.includes('cost of sales')) return 'costOfGoodsSold'
  if (typed.includes('other income')) return 'otherIncome'
  if (typed.includes('other expense')) return 'otherExpenses'
  if (accountType === 'income' || accountType === 'revenue' || classification === 'revenue' || classification === 'income') return 'income'
  if (accountType === 'expense' || classification === 'expense') return 'expenses'
  const accountNumber = String(row.account_number || row.label?.match(/^\s*(\d{4,})/)?.[1] || '')
  if (/^4/.test(accountNumber)) return 'income'
  if (/^5/.test(accountNumber)) return 'costOfGoodsSold'
  if (/^[67]/.test(accountNumber)) return 'expenses'
  if (/^8/.test(accountNumber)) return label.includes('income') || label.includes('earned') ? 'otherIncome' : 'otherExpenses'
  return null
}

const isGrossProfitForecastRow = (row: QuickBooksFpaStatementRow): boolean =>
  normalizeForecastRowLabel(row.label) === 'gross profit'

const isOperatingIncomeForecastRow = (row: QuickBooksFpaStatementRow): boolean => {
  const label = normalizeForecastRowLabel(row.label)
  return label === 'operating income' || label === 'net operating income'
}

const isNetOtherIncomeForecastRow = (row: QuickBooksFpaStatementRow): boolean => {
  const label = normalizeForecastRowLabel(row.label).replace(/&/g, 'and')
  return label === 'net other income' || label === 'other income and expenses'
}

const isNetIncomeForecastRow = (row: QuickBooksFpaStatementRow): boolean => {
  const label = normalizeForecastRowLabel(row.label)
  return label === 'net income' || label === 'net earnings'
}

const forecastRowValue = (row: QuickBooksFpaStatementRow | null | undefined, index: number): number | null => {
  const value = Number(row?.values?.[index])
  return Number.isFinite(value) ? value : null
}

const vectorFromRow = (row: QuickBooksFpaStatementRow | null | undefined, periodCount: number): Array<number | null> =>
  Array.from({ length: periodCount }, (_, index) => forecastRowValue(row, index))

const subtractVectors = (left: Array<number | null | undefined>, right: Array<number | null | undefined>): Array<number | null> =>
  addVectors(left, scaleVector(right, -1))

const fillBlankForecastValues = (
  primary: Array<number | null | undefined>,
  fallback: Array<number | null | undefined>,
): Array<number | null> =>
  Array.from({ length: Math.max(primary.length, fallback.length) }, (_, index) => {
    const value = Number(primary[index] || 0)
    if (Math.abs(value) > 0.005) return value
    const fallbackValue = Number(fallback[index] || 0)
    return Math.abs(fallbackValue) > 0.005 ? fallbackValue : null
  })

const bucketControlRow = (
  rows: QuickBooksFpaStatementRow[],
  bucket: ForecastProfitAndLossBucket,
): QuickBooksFpaStatementRow | null => {
  const statement = { rows } as QuickBooksFpaStatementRender
  if (bucket === 'income') return statementRowByLabels(statement, [['total', 'revenue'], ['total', 'income'], ['revenue'], ['income']])
  if (bucket === 'costOfGoodsSold') return statementRowByLabels(statement, [['total', 'cost', 'of', 'goods'], ['total', 'cost', 'of', 'sales'], ['cost', 'of', 'goods'], ['cost', 'of', 'sales'], ['cogs']])
  if (bucket === 'expenses') return statementRowByLabels(statement, [['total', 'expenses'], ['operating', 'expenses'], ['expenses'], ['expense']])
  if (bucket === 'otherIncome') return statementRowByLabels(statement, [['total', 'other', 'income'], ['other', 'income']])
  return statementRowByLabels(statement, [['total', 'other', 'expenses'], ['total', 'other', 'expense'], ['other', 'expenses'], ['other', 'expense']])
}

const bucketVector = (
  rows: QuickBooksFpaStatementRow[],
  bucket: ForecastProfitAndLossBucket,
  periodCount: number,
): Array<number | null> => {
  const control = bucketControlRow(rows, bucket)
  const parentIds = new Set(rows.map((row) => row.parent_node_id).filter((id): id is string => Boolean(id)))
  const derived = addVectors(...rows
    .filter((row) => {
      if (parentIds.has(row.node_id)) return false
      if (normalizeForecastRowLabel(row.row_type) === 'formula') return false
      return forecastProfitAndLossBucket(row) === bucket
    })
    .map((row) => row.values))
  if (control && hasStatementValue(control.values)) return fillBlankForecastValues(vectorFromRow(control, periodCount), derived)
  return derived
}

const capProfitAndLossBucketToRecentActualRange = (
  rows: QuickBooksFpaStatementRow[],
  bucket: ForecastProfitAndLossBucket,
  periodKinds: string[],
  forecastIndexes: number[],
  maxMultiple = 3,
) => {
  const periodCount = periodKinds.length
  const values = bucketVector(rows, bucket, periodCount)
  const firstForecastIndex = forecastIndexes[0] ?? periodCount
  const actualValues = periodKinds
    .map((kind, index) => (kind === 'actual' && index < firstForecastIndex ? Math.abs(Number(values[index] || 0)) : null))
    .filter((value): value is number => value != null && Number.isFinite(value) && value > 0.005)
    .slice(-3)
  if (!actualValues.length) return
  const anchor = actualValues.reduce((sum, value) => sum + value, 0) / actualValues.length
  if (!Number.isFinite(anchor) || anchor <= 0.005) return
  const limit = Math.max(anchor * maxMultiple, anchor + 100)
  const capped = values.map((value, index) => {
    if (!forecastIndexes.includes(index)) return value
    const numeric = Number(value)
    if (!Number.isFinite(numeric) || Math.abs(numeric) <= limit) return value
    return Math.sign(numeric || 1) * limit
  })
  scaleProfitAndLossBucket(rows, bucket, forecastIndexes, capped)
}

const localMlShapeFactor = (values: Array<number | null | undefined>, index: number, forecastIndexes: number[]): number => {
  const position = forecastIndexes.indexOf(index)
  if (position < 0) return 1
  const neighborIndexes = forecastIndexes.slice(Math.max(0, position - 1), Math.min(forecastIndexes.length, position + 2))
  const raw = Math.abs(Number(values[index] || 0))
  const average = neighborIndexes.reduce((sum, valueIndex) => sum + Math.abs(Number(values[valueIndex] || 0)), 0) / Math.max(1, neighborIndexes.length)
  if (!Number.isFinite(raw) || raw <= 0.005 || !Number.isFinite(average) || average <= 0.005) return 1
  return clampFactor(raw / average, 0.75, 1.25)
}

const driverAnchoredIncomeTargets = (
  row: QuickBooksFpaStatementRow | null,
  periodKinds: string[],
  forecastIndexes: number[],
  salesGrowth: number,
): Array<number | null> | null => {
  if (!row || !forecastIndexes.length) return null
  const values = row.values
  const firstForecastIndex = forecastIndexes[0]
  const lastActualIndex = periodKinds
    .map((kind, index) => (kind === 'actual' && index < firstForecastIndex ? index : -1))
    .reduce((max, index) => Math.max(max, index), -1)
  const firstShape = localMlShapeFactor(values, firstForecastIndex, forecastIndexes)
  const actualAnchor = lastActualIndex >= 0 ? Math.abs(Number(values[lastActualIndex] || 0)) : 0
  const forecastAnchor = Math.abs(Number(values[firstForecastIndex] || 0))
  const anchor = actualAnchor > 0.005 ? actualAnchor : forecastAnchor > 0.005 ? forecastAnchor / firstShape : 0
  if (!Number.isFinite(anchor) || anchor <= 0.005) return null
  const targets = values.map((value) => {
    const numeric = Number(value)
    return Number.isFinite(numeric) ? numeric : null
  })
  forecastIndexes.forEach((index, offset) => {
    const monthNumber = actualAnchor > 0.005 ? offset + 1 : offset
    const shape = localMlShapeFactor(values, index, forecastIndexes)
    targets[index] = anchor * annualGrowthFactor(salesGrowth, monthNumber) * shape
  })
  return targets
}

const scaleProfitAndLossBucket = (
  rows: QuickBooksFpaStatementRow[],
  bucket: ForecastProfitAndLossBucket,
  forecastIndexes: number[],
  targetValues: Array<number | null | undefined>,
) => {
  const control = bucketControlRow(rows, bucket)
  const parentIds = new Set(rows.map((row) => row.parent_node_id).filter((id): id is string => Boolean(id)))
  const fallbackValues = addVectors(...rows
    .filter((row) => !parentIds.has(row.node_id) && normalizeForecastRowLabel(row.row_type) !== 'formula' && forecastProfitAndLossBucket(row) === bucket)
    .map((row) => row.values))
  forecastIndexes.forEach((index) => {
    const target = Number(targetValues[index])
    const current = Number(control?.values?.[index] ?? fallbackValues[index] ?? 0)
    if (!Number.isFinite(target) || !Number.isFinite(current) || Math.abs(current) <= 0.005) return
    const factor = clampFactor(target / current, 0.05, 20)
    rows.forEach((row) => {
      if (normalizeForecastRowLabel(row.row_type) === 'formula') return
      if (control && row.node_id === control.node_id) return
      if (forecastProfitAndLossBucket(row) !== bucket) return
      const value = Number(row.values[index])
      if (Number.isFinite(value)) row.values[index] = value * factor
    })
    if (control) control.values[index] = target
  })
}

const setForecastRowsByPredicate = (
  rows: QuickBooksFpaStatementRow[],
  predicate: (row: QuickBooksFpaStatementRow) => boolean,
  values: Array<number | null | undefined>,
) => {
  rows.forEach((row) => {
    if (!predicate(row)) return
    row.values = values.map((value) => {
      const numeric = Number(value || 0)
      return Math.abs(numeric) > 0.005 ? numeric : null
    })
  })
}

export const applyProfitAndLossDriverCalibration = (
  statement: (QuickBooksFpaStatementRender & { period_kinds?: string[] }) | null,
  assumptions: any,
): (QuickBooksFpaStatementRender & { period_kinds?: string[] }) | null => {
  if (!statement || statement.statement_type !== 'profit_and_loss') return statement
  const periodKinds = statement.period_kinds || statement.periods.map(() => 'forecast')
  const forecastIndexes = periodKinds.map((kind, index) => kind === 'forecast' ? index : -1).filter((index) => index >= 0)
  if (!forecastIndexes.length) return statement
  const rows = statement.rows.map((row) => cloneRowWithValues(row, [...row.values]))
  const periodCount = statement.periods.length
  const incomeRow = bucketControlRow(rows, 'income')
  const incomeTargets = driverAnchoredIncomeTargets(incomeRow, periodKinds, forecastIndexes, driverNumber(assumptions, 'sales_growth_pct'))
  if (!incomeTargets) return statement

  scaleProfitAndLossBucket(rows, 'income', forecastIndexes, incomeTargets)
  const grossMargin = normalizeDriverRatio(driverFieldNumber(assumptions, 'gross_margin_pct'))
  if (grossMargin != null && grossMargin > 0 && grossMargin < 1.2) {
    scaleProfitAndLossBucket(rows, 'costOfGoodsSold', forecastIndexes, incomeTargets.map((value) => {
      const target = Number(value)
      return Number.isFinite(target) ? target * Math.max(0, 1 - grossMargin) : null
    }))
  }
  const opexPct = normalizeDriverRatio(driverFieldNumber(assumptions, 'opex_pct'))
  if (opexPct != null && opexPct >= 0 && opexPct < 2) {
    scaleProfitAndLossBucket(rows, 'expenses', forecastIndexes, incomeTargets.map((value) => {
      const target = Number(value)
      return Number.isFinite(target) ? target * opexPct : null
    }))
  }
  capProfitAndLossBucketToRecentActualRange(rows, 'otherIncome', periodKinds, forecastIndexes)
  capProfitAndLossBucketToRecentActualRange(rows, 'otherExpenses', periodKinds, forecastIndexes)

  const incomeValues = bucketVector(rows, 'income', periodCount)
  const cogsValues = bucketVector(rows, 'costOfGoodsSold', periodCount)
  const grossProfitValues = subtractVectors(incomeValues, cogsValues)
  const expenseValues = bucketVector(rows, 'expenses', periodCount)
  const operatingIncomeValues = subtractVectors(grossProfitValues, expenseValues)
  const otherIncomeValues = bucketVector(rows, 'otherIncome', periodCount)
  const otherExpenseValues = bucketVector(rows, 'otherExpenses', periodCount)
  const netOtherIncomeValues = subtractVectors(otherIncomeValues, otherExpenseValues)
  const netIncomeValues = addVectors(operatingIncomeValues, netOtherIncomeValues)

  setForecastRowsByPredicate(rows, isGrossProfitForecastRow, grossProfitValues)
  setForecastRowsByPredicate(rows, isOperatingIncomeForecastRow, operatingIncomeValues)
  setForecastRowsByPredicate(rows, isNetOtherIncomeForecastRow, netOtherIncomeValues)
  setForecastRowsByPredicate(rows, isNetIncomeForecastRow, netIncomeValues)

  return { ...statement, rows }
}

export type CashDriverDeltaOptions = {
  anchorReceiptsToActualGrowth?: boolean
}

export const applyCashDriverDeltas = (
  statement: (QuickBooksFpaStatementRender & { period_kinds?: string[] }) | null,
  currentAssumptions: any,
  baseAssumptions: any,
  options: CashDriverDeltaOptions = {},
): (QuickBooksFpaStatementRender & { period_kinds?: string[] }) | null => {
  if (!statement || statement.statement_type !== 'cash_flow' || !currentAssumptions) return statement
  const effectiveBaseAssumptions = baseAssumptions || currentAssumptions
  const periodKinds = statement.period_kinds || statement.periods.map(() => 'forecast')
  const forecastIndexes = periodKinds.map((kind, index) => kind === 'forecast' ? index : -1).filter((index) => index >= 0)
  if (!forecastIndexes.length) return statement
  const userDriverDelta = (key: string): number => {
    if (currentAssumptions?.[key]?.source !== 'user_override') return 0
    return driverNumber(currentAssumptions, key) - driverNumber(effectiveBaseAssumptions, key)
  }
  const currentSalesGrowth = driverNumber(currentAssumptions, 'sales_growth_pct')
  const baseSalesGrowth = driverNumber(effectiveBaseAssumptions, 'sales_growth_pct')
  const salesDelta = userDriverDelta('sales_growth_pct')
  const dsoDelta = userDriverDelta('dso_days')
  const dpoDelta = userDriverDelta('dpo_days')
  const opexDelta = userDriverDelta('opex_pct')
  const shouldAnchorReceipts = options.anchorReceiptsToActualGrowth === true && Number.isFinite(currentSalesGrowth)
  if (!shouldAnchorReceipts && ![salesDelta, dsoDelta, dpoDelta, opexDelta].some((value) => Math.abs(value) > 0.0001)) return statement
  const rows = statement.rows.map((row) => cloneRowWithValues(row, [...row.values]))
  const byId = new Map(rows.map((row) => [row.node_id, row]))
  const adjust = (nodeIds: string[], factor: number, indexes = forecastIndexes) => {
    nodeIds.forEach((nodeId) => {
      rows.forEach((row) => {
        if (row.node_id !== nodeId && !row.node_id.startsWith(`${nodeId}:basis:`) && !row.node_id.startsWith(`${nodeId}:source:`)) return
        indexes.forEach((index) => {
          const value = Number(row.values[index])
          if (Number.isFinite(value)) row.values[index] = value * factor
        })
      })
    })
  }
  const payableFactor = clampFactor(1 - dpoDelta / 90)
  const customer = byId.get('fg-forecast-cash-customer-receipts')
  const otherReceipts = byId.get('fg-forecast-cash-other-receipts')
  const receipts = byId.get('fg-forecast-cash-receipts')
  const payroll = byId.get('fg-forecast-cash-payroll')
  const vendors = byId.get('fg-forecast-cash-vendors')
  const debtTax = byId.get('fg-forecast-cash-debt-tax-capex')
  const otherDisb = byId.get('fg-forecast-cash-other-disbursements')
  const disb = byId.get('fg-forecast-cash-disbursements')
  const equity = byId.get('fg-forecast-cash-equity')
  const ownerEquity = byId.get('fg-forecast-cash-owner-equity')
  const net = byId.get('fg-forecast-cash-net-change')
  const beginningCash = byId.get('fg-forecast-cash-beginning-cash')
  const endingCash = byId.get('fg-forecast-cash-ending-cash')
  const sumDirectChildren = (parentNodeId: string, index: number): number =>
    rows
      .filter((row) => row.parent_node_id === parentNodeId)
      .reduce((sum, row) => sum + Number(row.values[index] || 0), 0)
  const valueFromChildrenOrSelf = (row: QuickBooksFpaStatementRow | undefined, index: number): number => {
    if (!row) return 0
    const childSum = sumDirectChildren(row.node_id, index)
    return Math.abs(childSum) > 0.005 ? childSum : Number(row.values[index] || 0)
  }
  const firstForecastIndex = forecastIndexes[0]
  const lastActualIndex = periodKinds
    .map((kind, index) => (kind === 'actual' && index < firstForecastIndex ? index : -1))
    .reduce((max, index) => Math.max(max, index), -1)
  const actualReceiptBase = lastActualIndex >= 0
    ? representativeRecentActualValue(receipts?.values || [], periodKinds, firstForecastIndex) ?? Number(receipts?.values[lastActualIndex] || 0)
    : 0
  // Per-period receipt scale factor, captured so disbursements can scale by the SAME factor
  // (preserving the server forecast's receipt-to-disbursement ratio). Stored BEFORE the DSO
  // edit, which is a receivables-timing effect that applies to receipts only.
  const receiptFactorByIndex = new Map<number, number>()
  forecastIndexes.forEach((index, offset) => {
    const currentCustomer = Number(customer?.values[index] || 0)
    const currentOther = Number(otherReceipts?.values[index] || 0)
    const currentReceipts = currentCustomer + currentOther
    if (!Number.isFinite(currentReceipts) || Math.abs(currentReceipts) <= 0.005) return
    let receiptFactor = annualGrowthFactor(currentSalesGrowth, offset + 1)
      / annualGrowthFactor(baseSalesGrowth, offset + 1)
    if (shouldAnchorReceipts && actualReceiptBase > 0.005) {
      receiptFactor = (actualReceiptBase * annualGrowthFactor(currentSalesGrowth, offset + 1)) / currentReceipts
    }
    receiptFactorByIndex.set(index, receiptFactor)
    adjust(['fg-forecast-cash-customer-receipts', 'fg-forecast-cash-other-receipts'], clampFactor(receiptFactor * (1 - dsoDelta / 90), 0.10, 2.50), [index])
  })
  const grossMargin = normalizeDriverRatio(driverFieldNumber(currentAssumptions, 'gross_margin_pct'))
  const opexPct = normalizeDriverRatio(driverFieldNumber(currentAssumptions, 'opex_pct'))
  const grossMarginIsDegenerate = grossMargin != null && grossMargin >= 0.995
  if (shouldAnchorReceipts && receiptFactorByIndex.size > 0) {
    // Disbursements: anchor the TOTAL to the recent ACTUAL cash-cost run-rate (parallel to the
    // receipt anchor), so net cash change tracks the real trend. The server's weekly-cash layer
    // collapses forecast disbursements for tenants that gross-up collections through contra /
    // clearing accounts (Stubbs: forecast disb ~$473K vs actual ~$1,068K — the ~$594K clearing
    // outflow that offsets the grossed-up receipts is missing from the projection), so receipts
    // stay grossed-up while disbursements don't → net +$330K/mo compounding to $13.3M. We rebuild
    // disbursements to the actual total and load the remainder onto VENDORS (where the collections-
    // clearing pass-through belongs), keeping payroll / debt / capex realistic in the export.
    const gmEditFlex = currentAssumptions?.gross_margin_pct?.source === 'user_override'
      ? (normalizeDriverRatio(driverFieldNumber(effectiveBaseAssumptions, 'gross_margin_pct')) ?? 0) - (grossMargin ?? 0)
      : 0
    const editFlex = clampFactor((1 + opexDelta) * (1 + gmEditFlex), 0.10, 3.00)
    const disbChildIds = ['fg-forecast-cash-payroll', 'fg-forecast-cash-vendors', 'fg-forecast-cash-debt-tax-capex', 'fg-forecast-cash-other-disbursements']
    const actualDisbBase = lastActualIndex >= 0
      ? representativeRecentActualValue(periodKinds.map((_k, i) => valueFromChildrenOrSelf(disb, i)), periodKinds, firstForecastIndex)
      : null
    if (actualDisbBase != null && actualDisbBase > 0.005) {
      forecastIndexes.forEach((index, offset) => {
        const currentTotal = valueFromChildrenOrSelf(disb, index)
        if (!Number.isFinite(currentTotal) || Math.abs(currentTotal) <= 0.005) return
        const targetTotal = -Math.abs(actualDisbBase * annualGrowthFactor(currentSalesGrowth, offset + 1)) * editFlex
        const currentVendors = valueFromChildrenOrSelf(vendors, index)
        if (Math.abs(currentVendors) <= 0.005) {
          // No vendors line to absorb the gap — scale all disbursements proportionally.
          adjust(disbChildIds, clampFactor(targetTotal / currentTotal, 0.10, 6.00), [index])
          return
        }
        // Keep payroll (flexed by opex) / debt / capex / other realistic; vendors absorbs the
        // remainder — incl the contra/clearing pass-through — flexed by DPO.
        const opexFactor = clampFactor(1 + opexDelta, 0.10, 3.00)
        const nonVendorKept = valueFromChildrenOrSelf(payroll, index) * opexFactor
          + valueFromChildrenOrSelf(debtTax, index)
          + valueFromChildrenOrSelf(otherDisb, index)
        const targetVendors = (targetTotal - nonVendorKept) * payableFactor
        if (opexDelta !== 0) adjust(['fg-forecast-cash-payroll'], opexFactor, [index])
        adjust(['fg-forecast-cash-vendors'], clampFactor(targetVendors / currentVendors, 0.05, 15.00), [index])
      })
    } else {
      // No actual disbursement base (fresh package, no closed months) — fall back to scaling all
      // disbursements by the receipt factor so the server forecast's net ratio is at least kept.
      forecastIndexes.forEach((index) => {
        const rf = receiptFactorByIndex.get(index)
        if (rf == null) return
        adjust(disbChildIds, clampFactor(rf * (1 + opexDelta) * (1 + gmEditFlex) * payableFactor, 0.10, 3.00), [index])
      })
    }
  } else if (!grossMarginIsDegenerate && grossMargin != null && grossMargin > 0 && grossMargin < 1.2 && opexPct != null && opexPct >= 0 && opexPct < 2) {
    // Non-anchored path (e.g. sub-monthly grain, no actual operating base): absolute GM/opex target.
    const targetOperatingCashPct = Math.max(0, 1 - grossMargin) + opexPct
    forecastIndexes.forEach((index) => {
      const receiptValue = valueFromChildrenOrSelf(receipts, index)
      const currentOperatingCash = valueFromChildrenOrSelf(payroll, index) + valueFromChildrenOrSelf(vendors, index)
      if (!Number.isFinite(receiptValue) || Math.abs(receiptValue) <= 0.005 || !Number.isFinite(currentOperatingCash) || Math.abs(currentOperatingCash) <= 0.005) return
      const targetOperatingCash = -Math.abs(receiptValue * targetOperatingCashPct * payableFactor)
      const operatingFactor = clampFactor(targetOperatingCash / currentOperatingCash, 0.10, 3.00)
      adjust(['fg-forecast-cash-vendors', 'fg-forecast-cash-payroll'], operatingFactor, [index])
    })
  } else {
    const opexFactor = clampFactor(1 + opexDelta)
    adjust(['fg-forecast-cash-vendors', 'fg-forecast-cash-payroll'], payableFactor * opexFactor)
  }
  forecastIndexes.forEach((index) => {
    rows.forEach((row) => {
      if (row.row_type !== 'group' || !row.node_id.includes(':basis:')) return
      const value = sumDirectChildren(row.node_id, index)
      row.values[index] = Math.abs(value) > 0.005 ? value : null
    })
    const customerValue = valueFromChildrenOrSelf(customer, index)
    const otherReceiptValue = valueFromChildrenOrSelf(otherReceipts, index)
    const payrollValue = valueFromChildrenOrSelf(payroll, index)
    const vendorValue = valueFromChildrenOrSelf(vendors, index)
    const debtTaxValue = valueFromChildrenOrSelf(debtTax, index)
    const otherDisbValue = valueFromChildrenOrSelf(otherDisb, index)
    const ownerEquityValue = valueFromChildrenOrSelf(ownerEquity, index)
    const rec = customerValue + otherReceiptValue
    const out = payrollValue + vendorValue + debtTaxValue + otherDisbValue
    if (receipts) receipts.values[index] = rec || null
    if (customer) customer.values[index] = customerValue || customer.values[index]
    if (otherReceipts) otherReceipts.values[index] = otherReceiptValue || otherReceipts.values[index]
    if (payroll) payroll.values[index] = payrollValue || payroll.values[index]
    if (vendors) vendors.values[index] = vendorValue || vendors.values[index]
    if (debtTax) debtTax.values[index] = debtTaxValue || debtTax.values[index]
    if (otherDisb) otherDisb.values[index] = otherDisbValue || otherDisb.values[index]
    if (ownerEquity) ownerEquity.values[index] = ownerEquityValue || ownerEquity.values[index]
    if (equity) equity.values[index] = ownerEquityValue || null
    if (disb) disb.values[index] = out || null
    if (net) net.values[index] = (rec + out + ownerEquityValue) || null
  })
  if (beginningCash && endingCash && net) {
    let rollingCash = Number(beginningCash.values[forecastIndexes[0]] || endingCash.values[Math.max(0, forecastIndexes[0] - 1)] || 0)
    forecastIndexes.forEach((index) => {
      beginningCash.values[index] = Number.isFinite(rollingCash) ? rollingCash : null
      rollingCash += Number(net.values[index] || 0)
      endingCash.values[index] = Number.isFinite(rollingCash) ? rollingCash : null
    })
  }
  return { ...statement, rows }
}

export const applyForecastDriverDeltas = (
  statement: (QuickBooksFpaStatementRender & { period_kinds?: string[] }) | null,
  currentAssumptions: any,
  baseAssumptions: any,
  options: CashDriverDeltaOptions = {},
): (QuickBooksFpaStatementRender & { period_kinds?: string[] }) | null => {
  if (statement?.statement_type === 'profit_and_loss') return applyProfitAndLossDriverCalibration(statement, currentAssumptions)
  return applyCashDriverDeltas(statement, currentAssumptions, baseAssumptions, options)
}

export const isDriverOverride = (assumptions: any, key: string): boolean =>
  assumptions?.[key]?.source === 'user_override'

export const hasDriverOverrides = (assumptions: any): boolean =>
  Object.keys(assumptions || {}).some((key) => isDriverOverride(assumptions, key))

export const stripDriverOverrides = (assumptions: any): any => {
  const next: Record<string, any> = {}
  Object.entries(assumptions || {}).forEach(([key, entry]) => {
    if ((entry as any)?.source === 'user_override') return
    next[key] = entry
  })
  return next
}

export const revertDriverAssumption = (assumptions: any, baselineAssumptions: any, key: string): any => ({
  ...(assumptions || {}),
  [key]: { ...(baselineAssumptions?.[key] || {}) },
})

export const revertAllDriverAssumptions = (assumptions: any, baselineAssumptions: any): any => {
  const next: Record<string, any> = { ...(assumptions || {}) }
  Object.keys(next).forEach((key) => {
    if (next[key]?.source !== 'user_override') return
    next[key] = { ...(baselineAssumptions?.[key] || {}) }
  })
  return next
}

export type TrailingDebounce<T extends unknown[]> = {
  schedule: (...args: T) => void
  flush: () => void
  cancel: () => void
  pending: () => boolean
}

export const createTrailingDebounce = <T extends unknown[]>(
  callback: (...args: T) => void,
  waitMs: number,
): TrailingDebounce<T> => {
  let timer: ReturnType<typeof setTimeout> | null = null
  let lastArgs: T | null = null
  const fire = () => {
    timer = null
    if (!lastArgs) return
    const args = lastArgs
    lastArgs = null
    callback(...args)
  }
  return {
    schedule: (...args: T) => {
      lastArgs = args
      if (timer != null) clearTimeout(timer)
      timer = setTimeout(fire, waitMs)
    },
    flush: () => {
      if (timer == null) return
      clearTimeout(timer)
      fire()
    },
    cancel: () => {
      if (timer != null) clearTimeout(timer)
      timer = null
      lastArgs = null
    },
    pending: () => timer != null,
  }
}

const STATEMENT_DIFF_EPSILON = 0.005

export type ForecastStatementDiff = {
  cellDeltas: Record<string, number>
  changedCellCount: number
  endingCashDelta: number | null
  netIncomeDelta: number | null
  hasChanges: boolean
}

const statementDiffRow = (
  statement: QuickBooksFpaStatementRender | null | undefined,
  nodeId: string,
  labelPattern: RegExp,
): QuickBooksFpaStatementRow | null =>
  statement?.rows?.find((row) => row.node_id === nodeId)
  || statement?.rows?.find((row) => labelPattern.test(row.label || ''))
  || null

const lastFinitePairDelta = (
  baseRow: QuickBooksFpaStatementRow | null,
  adjustedRow: QuickBooksFpaStatementRow | null,
): number | null => {
  if (!baseRow || !adjustedRow) return null
  const length = Math.max(baseRow.values?.length || 0, adjustedRow.values?.length || 0)
  for (let index = length - 1; index >= 0; index -= 1) {
    const base = Number(baseRow.values?.[index])
    const adjusted = Number(adjustedRow.values?.[index])
    if (Number.isFinite(base) || Number.isFinite(adjusted)) {
      return (Number.isFinite(adjusted) ? adjusted : 0) - (Number.isFinite(base) ? base : 0)
    }
  }
  return null
}

const summedPairDelta = (
  baseRow: QuickBooksFpaStatementRow | null,
  adjustedRow: QuickBooksFpaStatementRow | null,
): number | null => {
  if (!baseRow || !adjustedRow) return null
  const length = Math.max(baseRow.values?.length || 0, adjustedRow.values?.length || 0)
  let sum = 0
  let count = 0
  for (let index = 0; index < length; index += 1) {
    const base = Number(baseRow.values?.[index])
    const adjusted = Number(adjustedRow.values?.[index])
    if (!Number.isFinite(base) && !Number.isFinite(adjusted)) continue
    sum += (Number.isFinite(adjusted) ? adjusted : 0) - (Number.isFinite(base) ? base : 0)
    count += 1
  }
  return count ? sum : null
}

/**
 * Per-cell deltas between the model-baseline statement and the
 * driver-adjusted statement, keyed `${node_id}|${periodIndex}`. Ending cash is
 * reported at the last populated period (a balance), net income as the sum of
 * per-period deltas (a flow).
 */
export const computeStatementDiff = (
  baseStatement: QuickBooksFpaStatementRender | null | undefined,
  adjustedStatement: QuickBooksFpaStatementRender | null | undefined,
): ForecastStatementDiff => {
  const cellDeltas: Record<string, number> = {}
  let changedCellCount = 0
  const baseRowsById = new Map((baseStatement?.rows || []).map((row) => [row.node_id, row]))
  const periodCount = adjustedStatement?.periods?.length || 0
  for (const row of adjustedStatement?.rows || []) {
    const baseRow = baseRowsById.get(row.node_id)
    if (!baseRow) continue
    for (let index = 0; index < periodCount; index += 1) {
      const base = Number(baseRow.values?.[index])
      const adjusted = Number(row.values?.[index])
      if (!Number.isFinite(base) && !Number.isFinite(adjusted)) continue
      const delta = (Number.isFinite(adjusted) ? adjusted : 0) - (Number.isFinite(base) ? base : 0)
      if (Math.abs(delta) <= STATEMENT_DIFF_EPSILON) continue
      cellDeltas[`${row.node_id}|${index}`] = delta
      changedCellCount += 1
    }
  }
  const endingCashDelta = lastFinitePairDelta(
    statementDiffRow(baseStatement, 'fg-forecast-cash-ending-cash', /^ending cash$/i),
    statementDiffRow(adjustedStatement, 'fg-forecast-cash-ending-cash', /^ending cash$/i),
  )
  const netIncomeDelta = summedPairDelta(
    statementDiffRow(baseStatement, 'fg-forecast-net-income', /^net (income|earnings)$/i),
    statementDiffRow(adjustedStatement, 'fg-forecast-net-income', /^net (income|earnings)$/i),
  )
  return {
    cellDeltas,
    changedCellCount,
    endingCashDelta,
    netIncomeDelta,
    hasChanges: changedCellCount > 0,
  }
}
